/**
 * ClosingDisclosure - Main assembly component for the complete 5-page CD form
 *
 * This component assembles all pages and provides unified state management
 * through the ClosingDisclosureProvider context.
 */

import * as React from 'react';
import { cn } from '@/lib/utils';

import { ClosingDisclosureProvider } from '../hooks/ClosingDisclosureContext';
import { useClosingDisclosure } from '../hooks/useClosingDisclosure';
import type { ClosingDisclosureData } from '../types/closingDisclosureData';
import { CDPage1, CDPage2, CDPage3, CDPage4, CDPage5 } from './pages';
import type { CDPage1Data } from './pages/CDPage1';
import type { CDPage2Data } from './pages/CDPage2';
import type { CDPage3Data } from './pages/CDPage3';
import type { CDPage4Data } from './pages/CDPage4';
import type { CDPage5Data } from './pages/CDPage5';

/**
 * Props for the ClosingDisclosure component
 */
export interface ClosingDisclosureProps {
  /** Initial form data */
  initialData?: Partial<ClosingDisclosureData>;
  /** Whether fields are editable */
  editable?: boolean;
  /** Callback when data changes */
  onChange?: (data: ClosingDisclosureData) => void;
  /** Form ID for footer */
  formId?: string;
  /** Total number of pages (default 5) */
  totalPages?: number;
  /** Additional className for the container */
  className?: string;
  /** Which pages to render (default: all) */
  pages?: (1 | 2 | 3 | 4 | 5)[];
  /** Show page separators for screen display */
  showPageSeparators?: boolean;
}

/**
 * Inner component that renders the pages (uses context)
 */
function ClosingDisclosureInner({
  formId = 'CFPB Form H-25(A)',
  totalPages = 5,
  className,
  pages = [1, 2, 3, 4, 5],
  showPageSeparators = true,
}: Omit<ClosingDisclosureProps, 'initialData' | 'onChange'>) {
  const { formData, editable, updatePage } = useClosingDisclosure();

  // Page change handlers
  const handlePage1Change = React.useCallback(
    (data: Partial<CDPage1Data>) => {
      updatePage('page1', data);
    },
    [updatePage],
  );

  const handlePage2Change = React.useCallback(
    (data: Partial<CDPage2Data>) => {
      updatePage('page2', data);
    },
    [updatePage],
  );

  const handlePage3Change = React.useCallback(
    (data: Partial<CDPage3Data>) => {
      updatePage('page3', data);
    },
    [updatePage],
  );

  const handlePage4Change = React.useCallback(
    (data: Partial<CDPage4Data>) => {
      updatePage('page4', data);
    },
    [updatePage],
  );

  const handlePage5Change = React.useCallback(
    (data: Partial<CDPage5Data>) => {
      updatePage('page5', data);
    },
    [updatePage],
  );

  // Page separator component
  const PageSeparator = () =>
    showPageSeparators ? (
      <div className="my-8 border-t-4 border-dashed border-gray-300 print:hidden" />
    ) : null;

  return (
    <div className={cn('closing-disclosure', className)}>
      {pages.includes(1) && (
        <>
          <CDPage1
            data={formData.page1}
            editable={editable}
            onChange={handlePage1Change}
            totalPages={totalPages}
            formId={formId}
          />
          {pages.length > 1 && pages.indexOf(1) < pages.length - 1 && <PageSeparator />}
        </>
      )}

      {pages.includes(2) && (
        <>
          <CDPage2
            data={formData.page2}
            editable={editable}
            onChange={handlePage2Change}
            totalPages={totalPages}
            formId={formId}
          />
          {pages.indexOf(2) < pages.length - 1 && <PageSeparator />}
        </>
      )}

      {pages.includes(3) && (
        <>
          <CDPage3
            data={formData.page3}
            editable={editable}
            onChange={handlePage3Change}
            totalPages={totalPages}
            formId={formId}
          />
          {pages.indexOf(3) < pages.length - 1 && <PageSeparator />}
        </>
      )}

      {pages.includes(4) && (
        <>
          <CDPage4
            data={formData.page4}
            editable={editable}
            onChange={handlePage4Change}
            totalPages={totalPages}
            formId={formId}
          />
          {pages.indexOf(4) < pages.length - 1 && <PageSeparator />}
        </>
      )}

      {pages.includes(5) && (
        <CDPage5
          data={formData.page5}
          editable={editable}
          onChange={handlePage5Change}
          totalPages={totalPages}
          formId={formId}
        />
      )}
    </div>
  );
}

/**
 * ClosingDisclosure - Main component that wraps everything in a provider
 *
 * @example
 * // Basic usage
 * <ClosingDisclosure
 *   initialData={sampleData}
 *   editable={true}
 *   onChange={(data) => console.log('Data changed:', data)}
 * />
 *
 * @example
 * // Render specific pages only
 * <ClosingDisclosure
 *   initialData={sampleData}
 *   pages={[1, 2]}
 * />
 */
export function ClosingDisclosure({
  initialData,
  editable = false,
  onChange,
  ...innerProps
}: ClosingDisclosureProps) {
  return (
    <ClosingDisclosureProvider initialData={initialData} editable={editable} onChange={onChange}>
      <ClosingDisclosureInner editable={editable} {...innerProps} />
    </ClosingDisclosureProvider>
  );
}

ClosingDisclosure.displayName = 'ClosingDisclosure';

/**
 * Export page components for standalone use
 */
export { CDPage1, CDPage2, CDPage3, CDPage4, CDPage5 };
